import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { transactionService, TransactionFilters, CreateTransactionData } from '../services';

export const useTransactions = (filters?: TransactionFilters) => {
  const queryClient = useQueryClient();

  // Get transactions query
  const { data, isLoading, error } = useQuery({
    queryKey: ['transactions', filters],
    queryFn: async () => {
      const response = await transactionService.getTransactions(filters);
      return response.data;
    },
  });

  // Create transaction mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateTransactionData) => transactionService.createTransaction(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });

  // Refund transaction mutation
  const refundMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      transactionService.refundTransaction(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });

  return {
    transactions: data,
    isLoading,
    error,
    createTransaction: createMutation.mutateAsync,
    refundTransaction: refundMutation.mutateAsync,
    createMutation,
    refundMutation,
  };
};

export const useTransaction = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['transaction', id],
    queryFn: async () => {
      const response = await transactionService.getTransaction(id);
      return response.data;
    },
    enabled: !!id,
  });

  return {
    transaction: data,
    isLoading,
    error,
  };
};

export const useTransactionSummary = (filters?: TransactionFilters) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['transaction-summary', filters],
    queryFn: async () => {
      const response = await transactionService.getSummary(filters);
      return response.data;
    },
  });

  return {
    summary: data,
    isLoading,
    error,
  };
};
